import { Box, ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enUS as dateFnsEN } from 'date-fns/locale/en-US';
import { tr as dateFnsTR } from 'date-fns/locale/tr';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import { getI18n } from 'react-i18next';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import './App.css';
import { LayoutWithCart } from './components/LayoutWithCart';
import AuthProvider from './context/AuthContext';
import BalanceProvider from './context/BalanceContext';
import CartProvider from './context/CartContext';
import { ConfirmDialogProvider } from './context/ConfirmDialog';
import CreditCardProvider from './context/CreditCardContext';
import InAppProvider from './context/InApp';
import { useInApp } from './hooks/in-app';
import { useAuth } from './hooks/useAuth';
import { lightTheme } from './muiTheme';
import AccountInfosPage from './pages/AccountInfosPage';
import { AccountPage } from './pages/AccountPage';
import { BalancesPage } from './pages/Balances';
import { BalancesQueryPage } from './pages/BalancesQuery';
import Cart from './pages/Cart';
import FeaturedToursPage from './pages/FeaturedTours';
import { GuidancePage } from './pages/Guidance';
import { GuidanceTourPage } from './pages/GuidanceTour';
import { GuidanceTourAccommodationPage } from './pages/GuidanceTourAccommodation';
import { GuidanceTourCustomersPage } from './pages/GuidanceTourCustomers';
import { GuidanceTourHealthPage } from './pages/GuidanceTourHealth';
import { GuidanceTourHealthDialysisPage } from './pages/GuidanceTourHealthDialysisPage';
import { GuidanceTourHealthDisabilityPage } from './pages/GuidanceTourHealthDisability';
import { GuidanceTourHealthMedicinePage } from './pages/GuidanceTourHealthMedicinePage';
import { GuidanceTourPassportPage } from './pages/GuidanceTourPassport';
import { GuidanceTourPaymentPage } from './pages/GuidanceTourPaymentList';
import { GuidanceTourTransportationPage } from './pages/GuidanceTourTransportation';
import { GuidanceTourVisaPage } from './pages/GuidanceTourVisa';
import Home from './pages/Home';
import { LoginPage } from './pages/Login';
import Logout from './pages/Logout';
import NewAccountPage from './pages/NewAccountPage';
import OnlineTourDetailPrintOutPage from './pages/OnlineTourDetailPrintOutPage';
import { OrderAccommodationPage } from './pages/OrderAccommodation';
import OrderDetailPage from './pages/OrderDetail';
import { OrderInvoicePage } from './pages/OrderInvoice';
import OrderPassportPage from './pages/OrderPassport';
import OrdersPage from './pages/Orders';
import OrderTransportationPage from './pages/OrderTransportation';
import OrderVisaPage from './pages/OrderVisa';
import { PaymentPage } from './pages/Payment';
import { PaymentGWPage } from './pages/PaymentGW';
import PointsPage from './pages/Points';
import PwReset from './pages/PwReset';
import RedirectBalancesPage from './pages/RedirectBalancesPage';
import { ResultPage } from './pages/Result';
import SearchTourPage from './pages/SearchTourPage';
import TourDetailPage from './pages/TourDetail';
import TourPurchasePage from './pages/TourPurchase';
import Tours from './pages/Tours';
import { AccountLayout } from './components/AccountLayout';
import { OrderResultPage } from './pages/OrderResult';
import ReactGA from 'react-ga4';

declare let GA_TRACKING_ID: string;

ReactGA.initialize(GA_TRACKING_ID ? GA_TRACKING_ID : '-');
ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

let firstRouteChange = true;

function Routing() {
  const { setInApp } = useInApp();
  const [sp] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    if (sp.get('app') === '1') {
      setInApp(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstRouteChange) {
      firstRouteChange = false;
      return;
    }
    window.parent?.postMessage(
      {
        event: 'routeChange',
        location,
      },
      '*',
    );
  }, [location]);

  return (
    <Routes>
      <Route path="/logout" element={<Navigate to="/account/logout" />} />
      <Route path="/" element={<Navigate to="/home" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<LoginPage />} />
      <Route path="/reset" element={<PwReset />} />
      <Route path="/new-account" element={<NewAccountPage />} />
      <Route path="/login-without-account" element={<BalancesQueryPage />} />
      <Route path="balances" element={<BalancesPage />} />
      <Route path="/redirect-balances" element={<RedirectBalancesPage />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/payment-gw" element={<PaymentGWPage />} />
      <Route path="/payment-result" element={<ResultPage />} />

      <Route path="/search-tour" element={<SearchTourPage />} />
      <Route element={<LayoutWithCart />}>
        <Route path="/home" element={<Home />} />
        <Route path="/tours" element={<Tours />} />
        <Route path="/tours/:id" element={<TourDetailPage />} />
        <Route path="/tours/:id/purchase" element={<TourPurchasePage />} />

        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<AccountPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route
            path="orders/:salesIndividualId"
            element={<OrderDetailPage />}
          />
          <Route
            path="orders/:salesIndividualId/accommodation"
            element={<OrderAccommodationPage />}
          />
          <Route
            path="orders/:salesIndividualId/transportation"
            element={<OrderTransportationPage />}
          />
          <Route
            path="orders/:salesIndividualId/passport"
            element={<OrderPassportPage />}
          />
          <Route
            path="orders/:salesIndividualId/visa"
            element={<OrderVisaPage />}
          />
          <Route
            path="orders/:salesIndividualId/invoice"
            element={<OrderInvoicePage />}
          />
          <Route path="infos" element={<AccountInfosPage />} />
          <Route path="guidance">
            <Route path="" element={<GuidancePage />} />
            <Route path=":salesIndividualId" element={<GuidanceTourPage />} />
            <Route
              path=":salesIndividualId/customers"
              element={<GuidanceTourCustomersPage />}
            />
            <Route
              path=":salesIndividualId/accommodation"
              element={<GuidanceTourAccommodationPage />}
            />
            <Route
              path=":salesIndividualId/transportation"
              element={<GuidanceTourTransportationPage />}
            />
            <Route
              path=":salesIndividualId/visa"
              element={<GuidanceTourVisaPage />}
            />
            <Route
              path=":salesIndividualId/passport"
              element={<GuidanceTourPassportPage />}
            />
            <Route
              path=":salesIndividualId/payment"
              element={<GuidanceTourPaymentPage />}
            />
            <Route
              path=":salesIndividualId/health"
              element={<GuidanceTourHealthPage />}
            />
            <Route
              path=":salesIndividualId/health/disability"
              element={<GuidanceTourHealthDisabilityPage />}
            />
            <Route
              path=":salesIndividualId/health/dialysis"
              element={<GuidanceTourHealthDialysisPage />}
            />
            <Route
              path=":salesIndividualId/health/medicine"
              element={<GuidanceTourHealthMedicinePage />}
            />
          </Route>
          <Route path="points" element={<PointsPage />} />
          <Route path="logout" element={<Logout />} />
        </Route>
        <Route path="/cart" element={<Cart />} />
        <Route path="/order-result" element={<OrderResultPage />} />
      </Route>
      <Route
        path="tour-print-out/:id"
        element={<OnlineTourDetailPrintOutPage />}
      />
      <Route path="/most-preferred-tours" element={<FeaturedToursPage />} />
      <Route path="/featured-tours" element={<FeaturedToursPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const i18n = getI18n();
  const dateFnsLocale = i18n.language === 'tr' ? dateFnsTR : dateFnsEN;

  return (
    <ThemeProvider theme={lightTheme}>
      <SnackbarProvider>
        <AuthProvider>
          <CreditCardProvider>
            <BalanceProvider>
              <CartProvider>
                <ConfirmDialogProvider>
                  <InAppProvider>
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={dateFnsLocale}
                    >
                      <Box sx={{ px: 1 }}>
                        <Router>
                          <Routing />
                        </Router>
                      </Box>
                    </LocalizationProvider>
                  </InAppProvider>
                </ConfirmDialogProvider>
              </CartProvider>
            </BalanceProvider>
          </CreditCardProvider>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
